import { FolderOpen, Image, ListTree } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LanguageSelector } from './LanguageSelector';
import { ThemeToggle } from './ThemeToggle';
import { ConnectionStatusIndicator } from './ConnectionStatusIndicator';
import { EngiCoroLogo } from './EngiCoroLogo';
import { useTranslation } from '@/i18n/LanguageContext';
import { useIsMobile } from '@/hooks/use-mobile';
import type { ConnectionDebugInfo } from '@/hooks/useDocuments';
import { BRAND_NAVY, BRAND_SIDEBAR } from '@/constants/brandColors';

interface ViewOnlyTopBarProps {
  onLoad: () => void;
  onExportPNG: () => void;
  onPreview: () => void;
  isConnected?: boolean;
  connectionDebugInfo?: ConnectionDebugInfo;
}

export function ViewOnlyTopBar({ onLoad, onExportPNG, onPreview, isConnected, connectionDebugInfo }: ViewOnlyTopBarProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  return (
    <header className="h-20 flex items-center justify-between px-3 sm:px-5 shadow-lg" style={{ background: BRAND_SIDEBAR }} role="banner" data-testid="view-only-topbar">
      <div className="flex items-center gap-2 sm:gap-3 min-w-0">
        <EngiCoroLogo className={isMobile ? "h-14 flex-shrink-0" : "h-16 flex-shrink-0"} />
      </div>

      <div className="flex items-center gap-1 sm:gap-2">
        <ConnectionStatusIndicator isConnected={!!isConnected} debugInfo={connectionDebugInfo} />
        <Button variant="ghost" size="sm" onClick={onLoad} className="text-white hover:opacity-90 rounded-md" style={{ backgroundColor: BRAND_NAVY }} aria-label={t('load')} data-testid="viewbar-load">
          <FolderOpen className="w-4 h-4 mr-2" aria-hidden="true" />
          {!isMobile && t('load')}
        </Button>
        <Button variant="ghost" size="sm" onClick={onExportPNG} className="text-white hover:opacity-90 rounded-md" style={{ backgroundColor: BRAND_NAVY }} aria-label={t('exportPng')} data-testid="viewbar-export">
          <Image className="w-4 h-4 mr-2" aria-hidden="true" />
          {!isMobile && t('exportPng')}
        </Button>
        <Button variant="ghost" size="sm" onClick={onPreview} className="text-white hover:opacity-90 rounded-md" style={{ backgroundColor: BRAND_NAVY }} aria-label="Overview" data-testid="viewbar-preview">
          <ListTree className="w-4 h-4 mr-2" aria-hidden="true" />
          {!isMobile && 'Overview'}
        </Button>

        <ThemeToggle className="text-brand-navy hover:bg-brand-navy/10" />
        <LanguageSelector className="text-brand-navy hover:bg-brand-navy/10" />
      </div>
    </header>
  );
}
